import { FunctionComponent, ReactNode } from "react";

interface DataSecurityPageProps {}

interface SectionProps {
  title: string;
  children: ReactNode;
}

const Section: FunctionComponent<SectionProps> = ({ title, children }) => {
  return (
    <section className="flex w-full flex-col gap-4">
      <h2 className="font-GeistBold text-xl">{title}</h2>
      {children}
    </section>
  );
};

const ExternalLink: FunctionComponent<{
  href: string;
  children?: ReactNode;
}> = ({ href, children }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words underline underline-offset-2"
    >
      {children ?? href}
    </a>
  );
};

const DataSecurityPage: FunctionComponent<DataSecurityPageProps> = () => {
  return (
    <main className="mx-auto mt-[85px] flex w-full flex-col items-start justify-start px-4 pb-16 lg:w-1/3 lg:px-0">
      <header className="w-full py-6 text-left">
        <h1 className="font-GeistBold text-3xl">Datenschutzerklärung</h1>
      </header>

      <hr className="mb-8 h-[1px] w-full border-0 bg-slate-300" />

      <div className="flex w-full flex-col gap-10 text-base leading-relaxed">
        <section className="flex flex-col gap-4">
          <p>
            Soweit nachstehend keine anderen Angaben gemacht werden, ist die
            Bereitstellung Ihrer personenbezogenen Daten weder gesetzlich oder
            vertraglich vorgeschrieben, noch für einen Vertragsabschluss
            erforderlich. Sie sind zur Bereitstellung der Daten nicht
            verpflichtet. Eine Nichtbereitstellung hat keine Folgen. Dies gilt
            nur soweit bei den nachfolgenden Verarbeitungsvorgängen keine
            anderweitige Angabe gemacht wird.
          </p>

          <p>
            &quot;Personenbezogene Daten&quot; sind alle Informationen, die sich
            auf eine identifizierte oder identifizierbare natürliche Person
            beziehen.
          </p>
        </section>

        <Section title="Server-Logfiles">
          <p>
            Sie können unsere Webseiten besuchen, ohne Angaben zu Ihrer Person
            zu machen.
          </p>

          <p>
            Bei jedem Zugriff auf unsere Website werden an uns oder unseren
            Webhoster / IT-Dienstleister Nutzungsdaten durch Ihren
            Internetbrowser übermittelt und in Protokolldaten (sogenannte
            Server-Logfiles) gespeichert. Zu diesen gespeicherten Daten gehören
            zum Beispiel der Name der aufgerufenen Seite, Datum und Uhrzeit des
            Abrufs, die IP-Adresse, die übertragene Datenmenge und der
            anfragende Provider.
          </p>

          <p>
            Die Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1 lit. f
            DSGVO aus unserem überwiegenden berechtigten Interesse an der
            Gewährleistung eines störungsfreien Betriebs unserer Website sowie
            zur Verbesserung unseres Angebotes.
          </p>
        </Section>

        <section className="flex w-full flex-col gap-6">
          <h2 className="font-GeistBold text-2xl">Kontakt</h2>

          <Section title="Verantwortlicher">
            <p>Kontaktieren Sie uns auf Wunsch.</p>

            <address className="not-italic">
              <p>Verantwortlicher für die Datenverarbeitung ist:</p>

              <div className="mt-4">
                <p>Juwelier Bucher GbR</p>
                <p>Weinholdstraße 33</p>
                <p>08468 Reichenbach</p>
                <p>Deutschland</p>
              </div>

              <div className="mt-4">
                <p>
                  Telefon:{" "}
                  <a
                    href="tel:+49376512802"
                    className="underline underline-offset-2"
                  >
                    0376512802
                  </a>
                </p>

                <p>
                  E-Mail:{" "}
                  <a
                    href="mailto:info@juwelier-bucher.de"
                    className="underline underline-offset-2"
                  >
                    info@juwelier-bucher.de
                  </a>
                </p>
              </div>
            </address>
          </Section>

          <Section title="Initiativ-Kontaktaufnahme des Kunden per E-Mail">
            <p>
              Wenn Sie per E-Mail initiativ mit uns in Geschäftskontakt treten,
              erheben wir Ihre personenbezogenen Daten (Name, E-Mail-Adresse,
              Nachrichtentext) nur in dem von Ihnen zur Verfügung gestellten
              Umfang. Die Datenverarbeitung dient der Bearbeitung und
              Beantwortung Ihrer Kontaktanfrage.
            </p>

            <p>
              Wenn die Kontaktaufnahme der Durchführung vorvertraglichen
              Maßnahmen (beispielsweise Beratung bei Kaufinteresse,
              Angebotserstellung) dient oder einen bereits zwischen Ihnen und
              uns geschlossenen Vertrag betrifft, erfolgt diese
              Datenverarbeitung auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO.
            </p>

            <p>
              Erfolgt die Kontaktaufnahme aus anderen Gründen, erfolgt diese
              Datenverarbeitung auf Grundlage des Art. 6 Abs. 1 lit. f DSGVO aus
              unserem überwiegenden berechtigten Interesse an der Bearbeitung
              und Beantwortung Ihrer Anfrage. In diesem Fall haben Sie das
              Recht, aus Gründen, die sich aus Ihrer besonderen Situation
              ergeben, jederzeit dieser auf Art. 6 Abs. 1 lit. f DSGVO
              beruhenden Verarbeitung Sie betreffender personenbezogener Daten
              zu widersprechen.
            </p>

            <p>
              Ihre E-Mail-Adresse nutzen wir nur zur Bearbeitung Ihrer Anfrage.
              Ihre Daten werden anschließend unter Beachtung gesetzlicher
              Aufbewahrungsfristen gelöscht, sofern Sie der weitergehenden
              Verarbeitung und Nutzung nicht zugestimmt haben.
            </p>
          </Section>

          <Section title="Erhebung und Verarbeitung bei Nutzung des Kontaktformulars">
            <p>
              Bei der Nutzung des Kontaktformulars erheben wir Ihre
              personenbezogenen Daten (Name, E-Mail-Adresse, Nachrichtentext)
              nur in dem von Ihnen zur Verfügung gestellten Umfang. Die
              Datenverarbeitung dient dem Zweck der Kontaktaufnahme.
            </p>

            <p>
              Wenn die Kontaktaufnahme der Durchführung vorvertraglichen
              Maßnahmen (beispielsweise Beratung bei Kaufinteresse,
              Angebotserstellung) dient oder einen bereits zwischen Ihnen und
              uns geschlossenen Vertrag betrifft, erfolgt diese
              Datenverarbeitung auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO.
            </p>

            <p>
              Erfolgt die Kontaktaufnahme aus anderen Gründen, erfolgt diese
              Datenverarbeitung auf Grundlage des Art. 6 Abs. 1 lit. f DSGVO aus
              unserem überwiegenden berechtigten Interesse an der Bearbeitung
              und Beantwortung Ihrer Anfrage. In diesem Fall haben Sie das
              Recht, aus Gründen, die sich aus Ihrer besonderen Situation
              ergeben, jederzeit dieser auf Art. 6 Abs. 1 lit. f DSGVO
              beruhenden Verarbeitung Sie betreffender personenbezogener Daten
              zu widersprechen.
            </p>

            <p>
              Ihre E-Mail-Adresse nutzen wir nur zur Bearbeitung Ihrer Anfrage.
              Ihre Daten werden anschließend unter Beachtung gesetzlicher
              Aufbewahrungsfristen gelöscht, sofern Sie der weitergehenden
              Verarbeitung und Nutzung nicht zugestimmt haben.
            </p>
          </Section>

          <Section title="Erhebung und Verarbeitung bei Nutzung des Widerrufsbuttons">
            <p>
              Wenn Sie einen Vertrag über unsere Onlinepräsenz abgeschlossen
              haben, stellen wir Ihnen eine Widerrufsfunktion (Widerrufsbutton)
              zur Verfügung, über welche Sie Ihre Widerrufserklärung unmittelbar
              abgeben können.
            </p>

            <p>
              Bei der Nutzung der Widerrufsfunktion erheben wir Ihre
              personenbezogenen Daten (Name, E-Mail-Adresse, Angabe zur
              Identifizierung des Vertrages oder Vertragsteils, den Sie
              widerrufen möchten, sowie den Zeitpunkt – Datum und Uhrzeit – der
              Absendung der Widerrufserklärung) nur in dem von Ihnen zur
              Verfügung gestellten Umfang. Die Datenverarbeitung dient dem
              Zweck, Ihnen die gesetzlich vorgeschriebene Möglichkeit zum
              Widerruf Ihres Vertrages zur Verfügung zu stellen sowie der
              ordnungsgemäßen Bearbeitung Ihres Widerrufes.
            </p>

            <p>
              Wenn die Kontaktaufnahme einen bereits zwischen Ihnen und uns
              geschlossenen Vertrag betrifft, erfolgt diese Datenverarbeitung
              auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO. Ansonsten erfolgt
              die Datenverarbeitung auf Grundlage des Art. 6 Abs. 1 lit. c
              DSGVO, zur Erfüllung einer rechtlichen Verpflichtung, Ihnen eine
              Widerrufsfunktion auf unserer Onlinepräsenz zur Verfügung zu
              stellen.
            </p>

            <p>
              Ihre E-Mail-Adresse nutzen wir nur zur Bearbeitung Ihrer
              Widerrufserklärung. Ihre Daten werden anschließend unter Beachtung
              gesetzlicher Aufbewahrungsfristen gelöscht, sofern Sie der
              weitergehenden Verarbeitung und Nutzung nicht zugestimmt haben.
            </p>

            <p>
              Die Verarbeitung Ihrer personenbezogenen Daten dient dem Zweck,
              die gesetzlichen Anforderungen an die Gestaltung der
              Widerrufsfunktion rechtssicher zu erfüllen und erfolgt auf
              Grundlage des Art. 6 Abs. 1 lit. c DSGVO.
            </p>

            <p>
              Diese Datenverarbeitung erfolgt außerdem auf Grundlage des Art. 6
              Abs. 1 lit. f DSGVO aus unserem überwiegenden berechtigten
              Interesse, Ihnen eine benutzerfreundliche Widerrufsmöglichkeit zur
              Verfügung stellen zu können. In diesem Fall haben Sie das Recht,
              aus Gründen, die sich aus Ihrer besonderen Situation ergeben,
              jederzeit dieser auf Art. 6 Abs. 1 lit. f DSGVO beruhenden
              Verarbeitung Sie betreffender personenbezogener Daten zu
              widersprechen.
            </p>
          </Section>
        </section>

        <section className="flex w-full flex-col gap-6">
          <h2 className="font-GeistBold text-2xl">
            Kundenkonto und Bestellungen
          </h2>

          <Section title="Kundenkonto">
            <p>
              Bei der Eröffnung eines Kundenkontos erheben wir Ihre
              personenbezogenen Daten in dem dort angegebenen Umfang. Die
              Datenverarbeitung dient dem Zweck, Ihr Einkaufserlebnis zu
              verbessern und die Bestellabwicklung zu vereinfachen.
            </p>

            <p>
              Die Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1 lit. a
              DSGVO mit Ihrer Einwilligung. Sie können Ihre Einwilligung
              jederzeit durch Mitteilung an uns widerrufen, ohne dass die
              Rechtmäßigkeit der aufgrund der Einwilligung bis zum Widerruf
              erfolgten Verarbeitung berührt wird. Ihr Kundenkonto wird
              anschließend gelöscht.
            </p>
          </Section>

          <Section title="Erhebung, Verarbeitung und Weitergabe personenbezogener Daten bei Bestellungen">
            <p>
              Bei der Bestellung erheben und verarbeiten wir Ihre
              personenbezogenen Daten nur, soweit dies zur Erfüllung und
              Abwicklung Ihrer Bestellung sowie zur Bearbeitung Ihrer Anfragen
              erforderlich ist.
            </p>

            <p>
              Die Bereitstellung der Daten ist für den Vertragsschluss
              erforderlich. Eine Nichtbereitstellung hat zur Folge, dass kein
              Vertrag geschlossen werden kann. Die Verarbeitung erfolgt auf
              Grundlage des Art. 6 Abs. 1 lit. b DSGVO und ist für die Erfüllung
              eines Vertrags mit Ihnen erforderlich.
            </p>

            <p>
              Eine Weitergabe Ihrer Daten erfolgt dabei beispielsweise an
              Versandunternehmen, Dropshipping- beziehungsweise
              Fulfillment-Anbieter, Zahlungsdienstleister, Diensteanbieter für
              die Bestellabwicklung und IT-Dienstleister. In allen Fällen
              beachten wir strikt die gesetzlichen Vorgaben. Der Umfang der
              Datenübermittlung beschränkt sich auf ein Mindestmaß.
            </p>
          </Section>
        </section>

        <section className="flex w-full flex-col gap-6">
          <h2 className="font-GeistBold text-2xl">Warenwirtschaft</h2>

          <Section title="Nutzung eines externen Warenwirtschaftssystems">
            <p>
              Wir verwenden zur Vertragsabwicklung ein Warenwirtschaftssystem im
              Rahmen einer Auftragsverarbeitung. Dazu werden Ihre im Rahmen der
              Bestellung erhobenen personenbezogenen Daten an
            </p>

            <address className="not-italic">
              <p>AEB IT – SYSTEME GmbH</p>
              <p>Adam-Höfle-Weg 3</p>
              <p>D – 64673 Zwingenberg</p>
            </address>

            <p>übermittelt.</p>

            <p>
              Die Verarbeitung Ihrer personenbezogenen Daten dient dem Zweck,
              den mit Ihnen geschlossenen Vertrag zu erfüllen und erfolgt auf
              Grundlage des Art. 6 Abs. 1 lit. b DSGVO.
            </p>
          </Section>
        </section>

        <section className="flex w-full flex-col gap-6">
          <h2 className="font-GeistBold text-2xl">Zahlungsdienstleister</h2>

          <Section title="Verwendung von PayPal Express">
            <p>
              Wir verwenden auf unserer Website den Zahlungsdienst PayPal
              Express der PayPal (Europe) S.à.r.l. et Cie, S.C.A. (22-24
              Boulevard Royal L-2449, Luxemburg; &quot;PayPal&quot;).
            </p>

            <p>
              Die Datenverarbeitung dient dem Zweck, Ihnen die Zahlung über den
              Zahlungsdienst PayPal Express anbieten zu können. Zur Einbindung
              dieses Zahlungsdienstes ist es erforderlich, dass PayPal beim
              Aufruf der Website Daten (zum Beispiel IP-Adresse, Gerätetyp,
              Betriebssystem, Browsertyp, Standort Ihres Geräts) sammelt,
              speichert und analysiert. Hierzu können auch Cookies eingesetzt
              werden. Die Cookies ermöglichen die Wiedererkennung Ihres
              Browsers.
            </p>

            <p>
              Die Verarbeitung Ihrer personenbezogenen Daten erfolgt auf
              Grundlage des Art. 6 Abs. 1 lit. f DSGVO aus unserem überwiegenden
              berechtigten Interesse an einem kundenorientierten Angebot von
              verschiedenen Zahlarten. Sie haben das Recht, aus Gründen, die
              sich aus Ihrer besonderen Situation ergeben, jederzeit dieser
              Verarbeitung Sie betreffender personenbezogener Daten zu
              widersprechen.
            </p>

            <p>
              Mit Auswahl und Nutzung von PayPal Express werden die zur
              Zahlungsabwicklung erforderlichen Daten an PayPal übermittelt, um
              den Vertrag mit Ihnen mit der gewählten Zahlart erfüllen zu
              können. Diese Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1
              lit. b DSGVO.
            </p>

            <p>
              Nähere Informationen zur Datenverarbeitung bei Verwendung des
              Zahlungsdienstes PayPal Express finden Sie in der dazugehörigen
              Datenschutzerklärung unter{" "}
              <ExternalLink href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full?locale.x=de_DE#Updated_PS" />
              .
            </p>
          </Section>

          <Section title="Verwendung von PayPal Check-Out">
            <p>
              Wir verwenden auf unserer Website den Zahlungsdienst PayPal
              Check-Out der PayPal (Europe) S.à.r.l. et Cie, S.C.A. (22-24
              Boulevard Royal L-2449, Luxemburg; &quot;PayPal&quot;).
            </p>

            <p>
              Die Datenverarbeitung dient dem Zweck, Ihnen die Zahlung über den
              Zahlungsdienst anbieten zu können. Mit Auswahl und Nutzung von
              Zahlung via PayPal, Kreditkarte via PayPal, Lastschrift via PayPal
              oder &quot;Später Bezahlen&quot; via PayPal werden die zur
              Zahlungsabwicklung erforderlichen Daten an PayPal übermittelt, um
              den Vertrag mit Ihnen mit der gewählten Zahlart erfüllen zu
              können. Diese Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1
              lit. b DSGVO.
            </p>

            <p>
              Hierbei können Cookies gespeichert werden, die die Wiedererkennung
              Ihres Browsers ermöglichen. Die dadurch stattfindende
              Datenverarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1 lit. f
              DSGVO aus unserem überwiegenden berechtigten Interesse an einem
              kundenorientierten Angebot von verschiedenen Zahlarten. Sie haben
              das Recht, aus Gründen, die sich aus Ihrer besonderen Situation
              ergeben, jederzeit dieser Verarbeitung Sie betreffender
              personenbezogener Daten zu widersprechen.
            </p>
          </Section>

          <Section title='Kreditkarte via PayPal, Lastschrift via PayPal und "Später bezahlen" via PayPal'>
            <p>
              Bei einzelnen Zahlungsarten wie Kreditkarte via PayPal,
              Lastschrift via PayPal oder &quot;Später bezahlen&quot; via PayPal
              behält sich PayPal das Recht vor, gegebenenfalls eine
              Bonitätsauskunft auf der Basis mathematisch-statistischer
              Verfahren unter Nutzung von Auskunfteien einzuholen.
            </p>

            <p>
              Hierzu übermittelt PayPal die zu einer Bonitätsprüfung benötigten
              personenbezogenen Daten an eine Auskunftei und verwendet die
              erhaltenen Informationen über die statistische Wahrscheinlichkeit
              eines Zahlungsausfalls für eine abgewogene Entscheidung über die
              Begründung, Durchführung oder Beendigung des
              Vertragsverhältnisses.
            </p>

            <p>
              Die Bonitätsauskunft kann Wahrscheinlichkeitswerte (Score-Werte)
              beinhalten, die auf Basis wissenschaftlich anerkannter
              mathematisch-statistischer Verfahren berechnet werden und in deren
              Berechnung unter anderem Anschriftendaten einfließen. Ihre
              schutzwürdigen Belange werden gemäß den gesetzlichen Bestimmungen
              berücksichtigt.
            </p>

            <p>
              Die Datenverarbeitung dient dem Zweck der Bonitätsprüfung für eine
              Vertragsanbahnung. Die Verarbeitung erfolgt auf Grundlage des Art.
              6 Abs. 1 lit. f DSGVO aus unserem überwiegenden berechtigten
              Interesse am Schutz vor Zahlungsausfall, wenn PayPal in
              Vorleistung geht.
            </p>

            <p>
              Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen
              Situation ergeben, jederzeit gegen diese auf Art. 6 Abs. 1 lit. f
              DSGVO beruhende Verarbeitung Sie betreffender personenbezogener
              Daten durch Mitteilung an PayPal zu widersprechen.
            </p>

            <p>
              Die Bereitstellung der Daten ist für den Vertragsschluss mit der
              von Ihnen gewünschten Zahlart erforderlich. Eine
              Nichtbereitstellung hat zur Folge, dass der Vertrag nicht mit der
              von Ihnen gewählten Zahlart geschlossen werden kann.
            </p>
          </Section>

          <Section title="Drittanbieter">
            <p>
              Bei Bezahlung über die Zahlungsart eines Drittanbieters werden die
              zur Zahlungsabwicklung erforderlichen Daten an PayPal übermittelt.
              Diese Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1 lit. b
              DSGVO.
            </p>

            <p>
              Zur Durchführung dieser Zahlungsart werden die Daten
              gegebenenfalls sodann seitens PayPal an den jeweiligen Anbieter
              weitergegeben. Diese Verarbeitung erfolgt auf Grundlage des Art. 6
              Abs. 1 lit. b DSGVO.
            </p>

            <p>Lokale Drittanbieter können beispielsweise sein:</p>

            <ul className="list-disc space-y-2 pl-6">
              <li>
                Apple Pay (Apple Distribution International Ltd., Hollyhill
                Industrial Estate, Hollyhill, Cork, Irland)
              </li>

              <li>
                Google Pay (Google Ireland Limited, Gordon House, 4 Barrow St,
                Dublin, D04 E5W5, Irland)
              </li>
            </ul>
          </Section>

          <Section title="Rechnungskauf via PayPal">
            <p>
              Bei Bezahlung über die Zahlungsart Rechnungskauf werden die zur
              Zahlungsabwicklung erforderlichen Daten zunächst an PayPal
              übermittelt.
            </p>

            <p>
              Zur Durchführung dieser Zahlungsart werden die Daten sodann
              seitens PayPal an die Ratepay GmbH (Franklinstraße 28-29, 10587
              Berlin; &quot;Ratepay&quot;) übermittelt, um den Vertrag mit Ihnen
              mit der gewählten Zahlart erfüllen zu können. Diese Verarbeitung
              erfolgt auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO.
            </p>

            <p>
              Ratepay führt gegebenenfalls eine Bonitätsauskunft auf der Basis
              mathematisch-statistischer Verfahren (Wahrscheinlichkeits-
              beziehungsweise Score-Werte) unter Nutzung von Auskunfteien nach
              dem bereits oben beschriebenen Ablauf durch.
            </p>

            <p>
              Die Datenverarbeitung dient dem Zweck der Bonitätsprüfung für eine
              Vertragsanbahnung. Die Verarbeitung erfolgt auf Grundlage des Art.
              6 Abs. 1 lit. f DSGVO aus unserem überwiegenden berechtigten
              Interesse am Schutz vor Zahlungsausfall, wenn Ratepay in
              Vorleistung geht.
            </p>

            <p>
              Weitere Informationen zum Datenschutz und dazu, welche
              Auskunfteien Ratepay verwendet, finden Sie unter{" "}
              <ExternalLink href="https://www.ratepay.com/legal-payment-dataprivacy/" />{" "}
              und{" "}
              <ExternalLink href="https://www.ratepay.com/legal-payment-creditagencies/" />
              .
            </p>

            <p>
              Nähere Informationen zur Datenverarbeitung bei Verwendung von
              PayPal finden Sie in der dazugehörigen Datenschutzerklärung unter{" "}
              <ExternalLink href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full" />
              .
            </p>
          </Section>
        </section>

        <section className="flex w-full flex-col gap-6">
          <h2 className="font-GeistBold text-2xl">Cookies</h2>

          <section className="flex flex-col gap-4">
            <p>
              Unsere Website verwendet Cookies. Cookies sind kleine Textdateien,
              die im Internetbrowser beziehungsweise vom Internetbrowser auf dem
              Computersystem eines Nutzers gespeichert werden.
            </p>

            <p>
              Ruft ein Nutzer eine Website auf, so kann ein Cookie auf dem
              Betriebssystem des Nutzers gespeichert werden. Dieser Cookie
              enthält eine charakteristische Zeichenfolge, die eine eindeutige
              Identifizierung des Browsers beim erneuten Aufrufen der Website
              ermöglicht.
            </p>

            <p>
              Cookies werden auf Ihrem Rechner gespeichert. Daher haben Sie die
              volle Kontrolle über die Verwendung von Cookies. Durch die Auswahl
              entsprechender technischer Einstellungen in Ihrem Internetbrowser
              können Sie vor dem Setzen von Cookies benachrichtigt werden und
              über die Annahme einzeln entscheiden sowie die Speicherung der
              Cookies und Übermittlung der enthaltenen Daten verhindern.
            </p>

            <p>
              Bereits gespeicherte Cookies können jederzeit gelöscht werden. Wir
              weisen Sie jedoch darauf hin, dass Sie dann gegebenenfalls nicht
              sämtliche Funktionen dieser Website vollumfänglich nutzen können.
            </p>

            <p>
              Unter den nachstehenden Links können Sie sich informieren, wie Sie
              die Cookies bei den wichtigsten Browsern verwalten, unter anderem
              auch deaktivieren, können:
            </p>

            <ul className="flex list-disc flex-col gap-2 pl-6">
              <li>
                Chrome:{" "}
                <ExternalLink href="https://support.google.com/accounts/answer/61416?hl=de" />
              </li>

              <li>
                Microsoft Edge:{" "}
                <ExternalLink href="https://support.microsoft.com/de-de/microsoft-edge/cookies-in-microsoft-edge-l%C3%B6schen-63947406-40ac-c3b8-57b9-2a946a29ae09" />
              </li>

              <li>
                Mozilla Firefox:{" "}
                <ExternalLink href="https://support.mozilla.org/de/kb/cookies-erlauben-und-ablehnen" />
              </li>

              <li>
                Safari:{" "}
                <ExternalLink href="https://support.apple.com/de-de/guide/safari/manage-cookies-and-website-data-sfri11471/mac" />
              </li>
            </ul>
          </section>

          <Section title="Technisch notwendige Cookies">
            <p>
              Soweit nachstehend in der Datenschutzerklärung keine anderen
              Angaben gemacht werden, setzen wir nur diese technisch notwendigen
              Cookies zu dem Zweck ein, unser Angebot nutzerfreundlicher,
              effektiver und sicherer zu machen.
            </p>

            <p>
              Des Weiteren ermöglichen Cookies unseren Systemen, Ihren Browser
              auch nach einem Seitenwechsel zu erkennen und Ihnen Services
              anzubieten. Einige Funktionen unserer Internetseite können ohne
              den Einsatz von Cookies nicht angeboten werden. Für diese ist es
              erforderlich, dass der Browser auch nach einem Seitenwechsel
              wiedererkannt wird.
            </p>

            <p>
              Die Nutzung von Cookies oder vergleichbarer Technologien erfolgt
              auf Grundlage des § 25 Abs. 2 TDDDG. Die Verarbeitung Ihrer
              personenbezogenen Daten erfolgt auf Grundlage des Art. 6 Abs. 1
              lit. f DSGVO aus unserem überwiegenden berechtigten Interesse an
              der Gewährleistung der optimalen Funktionalität der Website sowie
              einer nutzerfreundlichen und effektiven Gestaltung unseres
              Angebots.
            </p>

            <p>
              Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen
              Situation ergeben, jederzeit dieser Verarbeitung Sie betreffender
              personenbezogener Daten zu widersprechen.
            </p>
          </Section>

          <Section title="Verwendung des Cookie Consent Managers CCM19">
            <p>
              Wir verwenden auf unserer Website den Cookie Consent Manager CCM19
              der HB legal tech GmbH (Kohlgartenstraße 11-13, 04315 Leipzig;
              &quot;CCM19&quot;) im Rahmen einer Auftragsverarbeitung.
            </p>

            <p>
              Das Plug-in wird auf{" "}
              <ExternalLink href="https://consenttool.haendlerbund.de/">
                consenttool.haendlerbund.de
              </ExternalLink>{" "}
              gehostet und ermöglicht Ihnen, Einwilligungen in
              Datenverarbeitungen über die Website, insbesondere das Setzen von
              Cookies, zu erteilen sowie von Ihrem Widerrufsrecht für bereits
              erteilte Einwilligungen Gebrauch zu machen.
            </p>

            <p>
              Die Datenverarbeitung dient dem Zweck, erforderliche
              Einwilligungen in Datenverarbeitungen einzuholen sowie zu
              dokumentieren und damit gesetzliche Verpflichtungen einzuhalten.
            </p>

            <p>
              Hierzu werden Cookies eingesetzt. Dabei können unter anderem
              folgende Informationen erhoben, gespeichert und gegebenenfalls an
              HB legal tech GmbH übertragen werden: zufällig vergebene ID,
              Einwilligungsstatus sowie Datum und Uhrzeit der Zustimmung
              beziehungsweise Ablehnung.
            </p>

            <p>
              Die Daten werden für 1 Jahr und einen Monat gespeichert und
              anschließend gelöscht. Eine Weitergabe dieser Daten an sonstige
              Dritte erfolgt nicht.
            </p>

            <p>
              Die Datenverarbeitung erfolgt zur Erfüllung einer rechtlichen
              Verpflichtung auf Grundlage des Art. 6 Abs. 1 lit. c DSGVO.
            </p>

            <p>
              Nähere Informationen zum Datenschutz finden Sie unter{" "}
              <ExternalLink href="https://www.haendlerbund.de/de/datenschutzerklaerung" />
              .
            </p>
          </Section>
        </section>

        <section className="flex w-full flex-col gap-6">
          <h2 className="font-GeistBold text-2xl">
            Betroffenenrechte und Speicherdauer
          </h2>

          <Section title="Dauer der Speicherung">
            <p>
              Nach vollständiger Vertragsabwicklung werden die Daten zunächst
              für die Dauer der Gewährleistungsfrist, danach unter
              Berücksichtigung gesetzlicher, insbesondere steuer- und
              handelsrechtlicher Aufbewahrungsfristen gespeichert und dann nach
              Fristablauf gelöscht, sofern Sie der weitergehenden Verarbeitung
              und Nutzung nicht zugestimmt haben.
            </p>
          </Section>

          <Section title="Rechte der betroffenen Person">
            <p>
              Ihnen stehen bei Vorliegen der gesetzlichen Voraussetzungen
              folgende Rechte nach Art. 15 bis 20 DSGVO zu: Recht auf Auskunft,
              auf Berichtigung, auf Löschung, auf Einschränkung der Verarbeitung
              und auf Datenübertragbarkeit.
            </p>

            <p>
              Außerdem steht Ihnen nach Art. 21 Abs. 1 DSGVO ein
              Widerspruchsrecht gegen die Verarbeitungen zu, die auf Art. 6 Abs.
              1 lit. f DSGVO beruhen, sowie gegen die Verarbeitung zum Zwecke
              von Direktwerbung.
            </p>
          </Section>

          <Section title="Beschwerderecht bei der Aufsichtsbehörde">
            <p>
              Sie haben gemäß Art. 77 DSGVO das Recht, sich bei der
              Aufsichtsbehörde zu beschweren, wenn Sie der Ansicht sind, dass
              die Verarbeitung Ihrer personenbezogenen Daten nicht rechtmäßig
              erfolgt.
            </p>

            <p>
              Eine Beschwerde können Sie unter anderem bei der für uns
              zuständigen Aufsichtsbehörde einlegen, die Sie unter folgenden
              Kontaktdaten erreichen:
            </p>

            <address className="not-italic">
              <p>Sächsische Datenschutz- und Transparenzbeauftragte</p>
              <p>Maternistraße 17</p>
              <p>01067 Dresden</p>

              <div className="mt-4">
                <p>
                  Tel.:{" "}
                  <a
                    href="tel:+4935185471101"
                    className="underline underline-offset-2"
                  >
                    +49 351 85471101
                  </a>
                </p>

                <p>
                  Fax:{" "}
                  <a
                    href="tel:+4935185471109"
                    className="underline underline-offset-2"
                  >
                    +49 351 85471109
                  </a>
                </p>

                <p>
                  E-Mail:{" "}
                  <a
                    href="mailto:post@sdtb.sachsen.de"
                    className="underline underline-offset-2"
                  >
                    post@sdtb.sachsen.de
                  </a>
                </p>
              </div>
            </address>
          </Section>

          <Section title="Widerspruchsrecht">
            <p>
              Beruhen die hier aufgeführten personenbezogenen
              Datenverarbeitungen auf Grundlage unseres berechtigten Interesses
              nach Art. 6 Abs. 1 lit. f DSGVO, haben Sie das Recht, aus Gründen,
              die sich aus Ihrer besonderen Situation ergeben, jederzeit diesen
              Verarbeitungen mit Wirkung für die Zukunft zu widersprechen.
            </p>

            <p>
              Nach erfolgtem Widerspruch wird die Verarbeitung der betroffenen
              Daten beendet, es sei denn, wir können zwingende schutzwürdige
              Gründe für die Verarbeitung nachweisen, die Ihren Interessen,
              Rechten und Freiheiten überwiegen, oder wenn die Verarbeitung der
              Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen
              dient.
            </p>
          </Section>
        </section>
      </div>
    </main>
  );
};

export default DataSecurityPage;
